import { DEFAULT_SHADOW_BORDER_COLOR } from 'constants/colors';

// convert hsl to rgba
export const hslToRgb = (h, s, l) => {
  s /= 100;
  l /= 100;

  const k = (n) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return { r: Math.round(f(0) * 255), g: Math.round(f(8) * 255), b: Math.round(f(4) * 255), a: 1 };
};

// convert rgb to hex
export const rgbToHex = (r, g, b) =>
  '#' +
  [r, g, b]
    .map((x) => x.toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase();

// convert hex to rgb
export const hexToRgb = (hex) => {
  // Remove the "#" if present
  hex = hex.replace(/^#/, '');

  // Convert shorthand hex (#abc) to full form (#aabbcc)
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((c) => c + c)
      .join('');
  }

  if (hex.length !== 6) {
    throw new Error('Invalid hex color');
  }

  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);

  return { r, g, b, a: 1 };
};

// convert rgb to hue
export const rgbToHue = (r, g, b) => {
  // Convert RGB (0–255) to range 0–1
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h;

  if (max === min) {
    h = 0; // achromatic
  } else {
    const d = max - min;
    // eslint-disable-next-line default-case
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h *= 60;
  }

  return Math.round(h); // 0–360
};

// check if the string is hex color code
export const isHexColor = (hex) => {
  return /^#([0-9A-Fa-f]{3}){1,2}$/.test(hex);
};

// Converts an rgb(a) object to an rgba() CSS string
export const objToRgba = ({ r = 0, g = 0, b = 0, a = 1 } = {}) => `rgba(${r},${g},${b},${a})`;

// Converts a shadow object to a CSS box-shadow string
export const getShadow = (shadow = {}) => {
  const { hueColor: { rgb } = {}, xOffset = 0, yOffset = 0, blur = 0, spread = 0 } = shadow;

  const color = objToRgba(rgb || DEFAULT_SHADOW_BORDER_COLOR.rgb);
  return `${color} ${xOffset}px ${yOffset}px ${blur}px ${spread}px`;
};

// Converts a border object to a CSS border string
export const getBorder = ({ borderWidth = 0, borderColor } = {}) => {
  const color = borderColor?.hex || DEFAULT_SHADOW_BORDER_COLOR.hex;
  return `${borderWidth}px solid ${color}`;
};
